import { BeGrid, BeWindow } from "@beos-css/react/index.ts";

/*
 * window_look -- the five looks the BeBook defines, plus B_NO_BORDER from the
 * header. Geometry is decomp-exact (Notes/06, SBeDecor object layout); the
 * BeBook one-liners are quoted in the notes under each window.
 */

const body: React.CSSProperties = { width: 260, height: 110 };

export function WindowLooksDemo() {
	return (
		<section id="demo-window-looks">
			<h2>Window looks (window_look)</h2>
			<p>
				<code>GenerateDecor</code> maps each look to a decorator and a flags word: bit0 = title tab, bit1 = resizable, bit2 = small. Two of
				these draw <strong>no title bar at all</strong>, and <code>B_BORDERED</code> is not even the same decorator class — it builds an{" "}
				<code>SThinDecor</code> whose only colour is <code>rgb(100,100,100)</code>.
			</p>
			<BeGrid min={20} align="start" gap="row">
				<div>
					<BeWindow title="Titled" onClose={() => {}} onZoom={() => {}} contentStyle={body}>
						“A normal window with a border and title bar.” 19px tab, 5px border, bold 12 title.
					</BeWindow>
				</div>

				<div>
					<BeWindow look="document" title="Document" onClose={() => {}} onZoom={() => {}} resizable="knob" contentStyle={body}>
						“Like a titled window, only with a resize dragger in the corner.” The dragger sits <em>inside</em> the content area — which is
						why scroll views leave that corner gap.
					</BeWindow>
				</div>

				<div>
					<BeWindow look="floating" title="Floating" onClose={() => {}} contentStyle={body}>
						“A titled window with a thin border and small title bar.” 15px tab, 12×12 glyphs, plain 10 title. Cortex mounts its transport
						this way — close box only, since it passes <code>B_NOT_ZOOMABLE</code>.
					</BeWindow>
				</div>

				<div>
					<BeWindow look="modal" contentStyle={body}>
						“A window with a border but no title bar.” Same 5px frame as titled, flags 0 — no tab, no knob.
					</BeWindow>
				</div>

				<div>
					<BeWindow look="bordered" contentStyle={body}>
						“This is a minimal window, with a thin border and little else.” One flat 1px rule, no bevel.
					</BeWindow>
				</div>

				<div>
					<BeWindow look="no-border" contentStyle={body}>
						<code>B_NO_BORDER_WINDOW_LOOK</code> — a bare <code>SWindowDecor</code> that draws nothing. Not one of the BeBook’s five.
					</BeWindow>
				</div>
			</BeGrid>
			<p>
				The floating frame is measured off an R5 VM capture of Cortex’s transport window, not derived from the titled one: three flat 1px
				rings (<code>[152][216][152]</code>, with <code>96</code> replacing the outer <code>152</code> on bottom/right) and no bevel at all.
				Its tab also carries <strong>no seam strip</strong> — sampling the rows under the tab and beside it gives an identical sequence, so
				it sits on an uninterrupted border rather than merging into the body. A floating window is never part of a tab stack, so it has no
				continuity to signal.
			</p>
		</section>
	);
}
